import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Users, Camera } from 'lucide-react';
import productsService from '../../services/productsService';
import PLACEHOLDER_IMAGE from '../../utils/placeholderImage';

/**
 * Hub for future UGC / hashtag / upload social proof for a single product.
 */
const ProductCommunity = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!slug) {
        setLoading(false);
        return;
      }
      try {
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug);
        let found = isUUID ? await productsService.getProduct(slug) : null;
        if (!found) {
          const all = await productsService.getProducts();
          found = all.find((p) => {
            const ps = p.slug || p.name?.toLowerCase().replace(/\s+/g, '-');
            return ps === slug || p.id === slug;
          });
          if (found) {
            const full = await productsService.getProduct(found.id);
            if (full) found = full;
          }
        }
        if (!cancelled) setProduct(found);
      } catch {
        if (!cancelled) setProduct(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const productSlug = product?.slug || slug;
  const hashtagFromSlug = (productSlug || '').replace(/-/g, '');
  const backHref = `/product/${productSlug}`;
  const cover =
    product?.primaryImage || product?.images?.[0] || product?.customImage || PLACEHOLDER_IMAGE;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
        paddingTop: 88,
        paddingBottom: 48,
      }}
    >
      <Helmet>
        <title>
          {product ? `${product.name} — Community | Rainbow Sails` : 'Community | Rainbow Sails'}
        </title>
        <meta
          name="description"
          content="Community photos and posts featuring this product. Share your own with a hashtag or upload."
        />
      </Helmet>

      <div style={{ maxWidth: 640, margin: '0 auto', padding: '0 20px' }}>
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(backHref))}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#fff',
            padding: '10px 14px',
            borderRadius: 10,
            cursor: 'pointer',
            marginBottom: 24,
          }}
        >
          <ArrowLeft size={18} />
          Back
        </button>

        {loading ? (
          <p style={{ color: '#9ca3af' }}>Loading…</p>
        ) : !product ? (
          <p style={{ color: '#fca5a5' }}>Product not found.</p>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 24 }}>
              <img
                src={cover}
                alt={product.name}
                style={{
                  width: 72,
                  height: 72,
                  objectFit: 'cover',
                  borderRadius: 12,
                  border: '1px solid rgba(255,255,255,0.12)',
                }}
              />
              <div>
                <h1 style={{ color: '#f9fafb', fontSize: 22, fontWeight: 700, margin: '0 0 6px' }}>
                  {product.name}
                </h1>
                <p style={{ color: '#9ca3af', fontSize: 14, margin: 0 }}>
                  Community photos &amp; social posts
                </p>
              </div>
            </div>

            <div
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 16,
                padding: 24,
                marginBottom: 24,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <Users size={22} color="#34d399" aria-hidden />
                <h2 style={{ color: '#e5e7eb', fontSize: 16, fontWeight: 600, margin: 0 }}>
                  Share a photo with your piece
                </h2>
              </div>
              <ol style={{ color: '#d1d5db', fontSize: 15, lineHeight: 1.65, margin: '0 0 16px', paddingLeft: 20 }}>
                <li style={{ marginBottom: 8 }}>
                  Post a public photo or short video of you with this product (wear it, unbox it, or show it in your space).
                </li>
                <li style={{ marginBottom: 8 }}>
                  In the caption, include{' '}
                  <strong style={{ color: '#f9fafb' }}>#RainbowSails</strong>
                  {hashtagFromSlug ? (
                    <>
                      {' '}
                      and <strong style={{ color: '#f9fafb' }}>#{hashtagFromSlug}</strong>
                    </>
                  ) : null}{' '}
                  so we can find your post. You can tag our shop account if you use one.
                </li>
                <li>
                  Optional: copy the link to your post and keep it handy—soon you will be able to paste it here so it
                  appears in a gallery for this product.
                </li>
              </ol>
              <p style={{ color: '#9ca3af', fontSize: 14, lineHeight: 1.55, margin: 0 }}>
                A live grid of customer shots is in the works; hashtags and this hub are the first step.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Link
                to={backHref}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: '14px 18px',
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  color: '#fff',
                  fontWeight: 600,
                  textDecoration: 'none',
                  fontSize: 15,
                }}
              >
                <Camera size={18} aria-hidden />
                View product &amp; gallery
              </Link>
              <Link
                to="/social"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: '14px 18px',
                  borderRadius: 12,
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#e5e7eb',
                  fontWeight: 500,
                  textDecoration: 'none',
                  fontSize: 15,
                  background: 'rgba(255,255,255,0.06)',
                }}
              >
                Browse Zee Social stream
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductCommunity;
